import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface ToggleCardProps {
  label: string;
  description: string;
  field: string;
  enabled: boolean;
  onToggle: (field: string, value: boolean) => void;
  saving: boolean;
}

const ToggleCard: React.FC<ToggleCardProps> = ({
  label,
  description,
  field,
  enabled,
  onToggle,
  saving,
}) => (
  <div className="notification-card">
    <div className="notification-card-info">
      <p className="notification-card-label">{label}</p>
      <p className="notification-card-desc">{description}</p>
    </div>
    <div className="notification-card-toggle">
      <button
        className={`toggle-switch ${enabled ? 'toggle-switch-on' : ''}`}
        onClick={() => onToggle(field, !enabled)}
        disabled={saving}
        aria-label={`${enabled ? 'Disable' : 'Enable'} ${label}`}
      >
        <span className="toggle-switch-knob" />
      </button>
      {saving && <span className="toggle-saving">Saving...</span>}
    </div>
  </div>
);

const NotificationsSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [morningBrief, setMorningBrief] = useState(false);
  const [dailyDigest, setDailyDigest] = useState(false);
  const [savingField, setSavingField] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getUserPreferences()
      .then((prefs) => {
        setMorningBrief(!!prefs.morning_brief_enabled);
        setDailyDigest(!!prefs.daily_digest_enabled);
      })
      .catch(() => {
        // defaults remain false
      })
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = useCallback(async (field: string, value: boolean) => {
    if (savingField) return;
    setSavingField(field);

    // Optimistic update
    if (field === 'morning_brief_enabled') setMorningBrief(value);
    if (field === 'daily_digest_enabled') setDailyDigest(value);

    try {
      await apiService.saveUserPreferences({ [field]: value });
    } catch {
      // Revert on failure
      if (field === 'morning_brief_enabled') setMorningBrief(!value);
      if (field === 'daily_digest_enabled') setDailyDigest(!value);
    } finally {
      setSavingField(null);
    }
  }, [savingField]);

  if (loading) {
    return (
      <div className="notifications-settings">
        <div className="notification-card app-skeleton" style={{ height: 80 }} />
        <div className="notification-card app-skeleton" style={{ height: 80 }} />
      </div>
    );
  }

  return (
    <div className="notifications-settings">
      <p className="notifications-settings-subtitle">
        Choose which email notifications you'd like to receive.
      </p>
      <ToggleCard
        label="Morning Brief"
        description="Daily briefing at 10:30 AM ET with live portfolio data"
        field="morning_brief_enabled"
        enabled={morningBrief}
        onToggle={handleToggle}
        saving={savingField === 'morning_brief_enabled'}
      />
      <ToggleCard
        label="Daily Digest"
        description="End-of-day recap at 6:00 PM ET with performance summary"
        field="daily_digest_enabled"
        enabled={dailyDigest}
        onToggle={handleToggle}
        saving={savingField === 'daily_digest_enabled'}
      />
    </div>
  );
};

export default NotificationsSettings;
